import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MTDDashboard as MTDDashboardType, MTDIncome, MTDExpense, MTDQuarterlyUpdate } from '../types/mtd';
import ThemeBackground from '../components/Layout/ThemeBackground';
import api from '../utils/api';
import { todayDateOnlyString } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';

// --- Shared styling ---

const dialogPaperSx = {
  backgroundColor: '#1e293b',
  color: '#f8fafc',
  backgroundImage: 'linear-gradient(to bottom right, #1e293b, #0f172a)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '20px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
};

const inputSx = {
  '& .MuiOutlinedInput-root': { color: '#f8fafc' },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' }
};

const StatCard = ({ title, amount, type, icon: Icon }: { title: string, amount: string, type: 'success' | 'error' | 'info' | 'warning', icon: any }) => {
  const colors = {
    success: { text: '#4ade80', bg: 'rgba(74, 222, 128, 0.1)', border: 'rgba(74, 222, 128, 0.2)' },
    error: { text: '#f87171', bg: 'rgba(248, 113, 113, 0.1)', border: 'rgba(248, 113, 113, 0.2)' },
    info: { text: '#60a5fa', bg: 'rgba(96, 165, 250, 0.1)', border: 'rgba(96, 165, 250, 0.2)' },
    warning: { text: '#fbbf24', bg: 'rgba(251, 191, 36, 0.1)', border: 'rgba(251, 191, 36, 0.2)' }
  };
  const style = colors[type];
  return (
    <Box sx={{
      background: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: '16px',
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-2px)' }
    }}>
      <Box display="flex" alignItems="center" mb={1} gap={1}>
        <Box p={0.5} borderRadius="8px" bgcolor={style.bg}>
          <Icon sx={{ color: style.text, fontSize: 20 }} />
        </Box>
        <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 700, letterSpacing: '-0.5px' }}>
        {amount}
      </Typography>
    </Box>
  );
};

const INCOME_TYPES = [
  { value: 'counselling_session', label: 'Counselling Session' },
  { value: 'supervision', label: 'Supervision' },
  { value: 'training', label: 'Training' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'consultancy', label: 'Consultancy' },
  { value: 'other', label: 'Other' }
];

const EXPENSE_TYPES = [
  { value: 'professional_fees', label: 'Professional Fees' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'supervision', label: 'Supervision' },
  { value: 'training', label: 'Training' },
  { value: 'cpd', label: 'CPD' },
  { value: 'room_hire', label: 'Room Hire' },
  { value: 'home_office', label: 'Home Office' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'travel', label: 'Travel' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'stationery', label: 'Stationery' },
  { value: 'software', label: 'Software' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'other', label: 'Other' }
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' }
];

interface HmrcStatus {
  configured: boolean;
  connected: boolean;
  ninoSet: boolean;
  businessId: string | null;
  businessName: string | null;
  connectedAt: string | null;
}

const MTDDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';

  const [entity, setEntity] = useState<'counsellor' | 'organisation'>(isAdmin ? 'organisation' : 'counsellor');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<MTDDashboardType | null>(null);
  const [hmrcStatus, setHmrcStatus] = useState<HmrcStatus | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });

  // Records tab
  const [recordsTab, setRecordsTab] = useState<'income' | 'expense'>('income');
  const [incomeRecords, setIncomeRecords] = useState<MTDIncome[]>([]);
  const [expenseRecords, setExpenseRecords] = useState<MTDExpense[]>([]);

  // Dialogs
  const [incomeDialog, setIncomeDialog] = useState<{ open: boolean; editing: MTDIncome | null }>({ open: false, editing: null });
  const [expenseDialog, setExpenseDialog] = useState<{ open: boolean; editing: MTDExpense | null }>({ open: false, editing: null });
  const [ninoDialog, setNinoDialog] = useState(false);
  const [ninoValue, setNinoValue] = useState('');
  const [businessDialog, setBusinessDialog] = useState(false);
  const [businesses, setBusinesses] = useState<Array<{ businessId: string; businessName?: string }>>([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');

  // Quarter details + submission
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [quarterDetailsOpen, setQuarterDetailsOpen] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [incomeForm, setIncomeForm] = useState({ date: '', incomeType: 'counselling_session', grossAmount: '', description: '', invoiceNumber: '', paymentMethod: 'bank_transfer', paymentReceived: true });
  const [expenseForm, setExpenseForm] = useState({ date: '', expenseType: 'professional_fees', amount: '', supplier: '', description: '', receiptUrl: '', hasReceipt: false, allowablePercentage: '100' });

  const notify = (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashRes, statusRes, incomeRes, expenseRes] = await Promise.all([
        api.get('/api/mtd/dashboard', { params: { entity } }),
        api.get('/api/mtd/hmrc/status', { params: { entity } }),
        api.get('/api/mtd/income', { params: { entity } }),
        api.get('/api/mtd/expense', { params: { entity } })
      ]);
      if (dashRes.data.success) setDashboardData(dashRes.data.data);
      setHmrcStatus(statusRes.data);
      setIncomeRecords(incomeRes.data.income || []);
      setExpenseRecords(expenseRes.data.expenses || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [entity]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // Surface the OAuth redirect outcome once, then clean the URL.
  useEffect(() => {
    const hmrc = searchParams.get('hmrc');
    if (hmrc === 'connected') notify('Connected to HMRC successfully');
    else if (hmrc === 'denied') notify('HMRC connection was declined', 'error');
    else if (hmrc === 'error') notify('HMRC connection failed', 'error');
    if (hmrc) {
      searchParams.delete('hmrc');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'fulfilled': return 'success';
      case 'overdue': return 'error';
      case 'open': return 'warning';
      default: return 'default';
    }
  };

  // --- HMRC actions ---

  const connectHmrc = async () => {
    try {
      const res = await api.get('/api/mtd/hmrc/connect', { params: { entity } });
      window.location.href = res.data.url;
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not start HMRC connection', 'error');
    }
  };

  const saveNino = async () => {
    try {
      await api.put('/api/mtd/hmrc/nino', { entity, nino: ninoValue });
      setNinoDialog(false);
      setNinoValue('');
      notify('National Insurance number saved');
      fetchDashboard();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Invalid NINO', 'error');
    }
  };

  const loadBusinesses = async () => {
    try {
      const res = await api.get('/api/mtd/hmrc/businesses', { params: { entity } });
      setBusinesses(res.data.businesses || []);
      setBusinessDialog(true);
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not fetch HMRC businesses', 'error');
    }
  };

  const saveBusiness = async () => {
    try {
      const biz = businesses.find(b => b.businessId === selectedBusiness);
      await api.post('/api/mtd/hmrc/business', { entity, businessId: selectedBusiness, businessName: biz?.businessName });
      setBusinessDialog(false);
      notify('HMRC business selected');
      fetchDashboard();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not save business', 'error');
    }
  };

  const disconnectHmrc = async () => {
    await api.post('/api/mtd/hmrc/disconnect', { entity });
    notify('Disconnected from HMRC', 'info');
    fetchDashboard();
  };

  // --- Record CRUD ---

  const openIncomeDialog = (record: MTDIncome | null = null) => {
    setIncomeForm(record ? {
      date: record.date.slice(0, 10),
      incomeType: record.incomeType,
      grossAmount: String(record.grossAmount),
      description: record.description,
      invoiceNumber: record.invoiceNumber || '',
      paymentMethod: record.paymentMethod,
      paymentReceived: record.paymentReceived
    } : { date: todayDateOnlyString(), incomeType: 'counselling_session', grossAmount: '', description: '', invoiceNumber: '', paymentMethod: 'bank_transfer', paymentReceived: true });
    setIncomeDialog({ open: true, editing: record });
  };

  const saveIncome = async () => {
    try {
      const payload = { entity, ...incomeForm, grossAmount: Number(incomeForm.grossAmount) };
      if (incomeDialog.editing) {
        await api.put(`/api/mtd/income/${incomeDialog.editing._id}`, payload);
        notify('Income updated');
      } else {
        await api.post('/api/mtd/income', payload);
        notify('Income recorded');
      }
      setIncomeDialog({ open: false, editing: null });
      fetchDashboard();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not save income', 'error');
    }
  };

  const deleteIncome = async (id: string) => {
    try {
      await api.delete(`/api/mtd/income/${id}`, { params: { entity } });
      notify('Income deleted', 'info');
      fetchDashboard();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not delete income', 'error');
    }
  };

  const openExpenseDialog = (record: MTDExpense | null = null) => {
    setExpenseForm(record ? {
      date: record.date.slice(0, 10),
      expenseType: record.expenseType,
      amount: String(record.amount),
      supplier: record.supplier,
      description: record.description,
      receiptUrl: record.receiptUrl || '',
      hasReceipt: record.hasReceipt,
      allowablePercentage: String(record.allowablePercentage)
    } : { date: todayDateOnlyString(), expenseType: 'professional_fees', amount: '', supplier: '', description: '', receiptUrl: '', hasReceipt: false, allowablePercentage: '100' });
    setExpenseDialog({ open: true, editing: record });
  };

  const saveExpense = async () => {
    try {
      const payload = { entity, ...expenseForm, amount: Number(expenseForm.amount), allowablePercentage: Number(expenseForm.allowablePercentage) };
      if (expenseDialog.editing) {
        await api.put(`/api/mtd/expense/${expenseDialog.editing._id}`, payload);
        notify('Expense updated');
      } else {
        await api.post('/api/mtd/expense', payload);
        notify('Expense recorded');
      }
      setExpenseDialog({ open: false, editing: null });
      fetchDashboard();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not save expense', 'error');
    }
  };

  const deleteExpense = async (id: string) => {
    try {
      await api.delete(`/api/mtd/expense/${id}`, { params: { entity } });
      notify('Expense deleted', 'info');
      fetchDashboard();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Could not delete expense', 'error');
    }
  };

  // --- Quarterly submission ---

  const submitQuarter = async () => {
    if (selectedQuarter === null || !dashboardData) return;
    try {
      setSubmitting(true);
      await api.post(`/api/mtd/quarterly/${dashboardData.currentTaxYear}/${selectedQuarter}/submit`, { entity });
      setSubmitConfirm(false);
      setQuarterDetailsOpen(false);
      notify(`Q${selectedQuarter} submitted to HMRC`);
      fetchDashboard();
    } catch (err: any) {
      setSubmitConfirm(false);
      notify(err.response?.data?.error || 'Submission failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/api/mtd/export.csv', {
        params: { entity, taxYear: dashboardData?.currentTaxYear },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mtd-${entity}-${dashboardData?.currentTaxYear || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      notify('Export failed', 'error');
    }
  };

  if (loading && !dashboardData) {
    return (
      <ThemeBackground>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#fff' }} />
        </Box>
      </ThemeBackground>
    );
  }

  if (error) {
    return (
      <ThemeBackground>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>{error}</Alert>
        </div>
      </ThemeBackground>
    );
  }

  if (!dashboardData) return null;

  const { currentTaxYear, currentQuarter, yearToDate, obligations, compliance, vat } = dashboardData;
  const readyToSubmit = hmrcStatus?.connected && hmrcStatus.ninoSet && hmrcStatus.businessId;

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll h-full">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)] relative z-10">
          <div className="max-w-7xl mx-auto py-2">

            {/* Header */}
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div className="flex flex-col space-y-4">
                    <div className="flex space-x-4 flex-wrap gap-2">
                      <Button variant="contained" sx={{ textTransform: 'none', fontWeight: 700, minWidth: '160px', backgroundColor: '#2196f3', '&:hover': { backgroundColor: '#1976d2' } }} onClick={() => navigate('/')}>
                        Back to Home
                      </Button>
                      <Button variant="contained" sx={{ textTransform: 'none', fontWeight: 700, minWidth: '120px', backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }} onClick={fetchDashboard} startIcon={<RefreshIcon />}>
                        Refresh
                      </Button>
                      <Button variant="contained" sx={{ textTransform: 'none', fontWeight: 700, minWidth: '140px', backgroundColor: '#00897b', '&:hover': { backgroundColor: '#00695c' } }} onClick={exportCsv} startIcon={<DownloadIcon />}>
                        Export CSV
                      </Button>
                    </div>
                    <div className="flex space-x-4">
                      <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none', fontWeight: 700, minWidth: '160px', backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }} onClick={() => openIncomeDialog()}>
                        Add Income
                      </Button>
                      <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none', fontWeight: 700, minWidth: '160px', backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' } }} onClick={() => openExpenseDialog()}>
                        Add Expense
                      </Button>
                    </div>
                  </div>

                  <div className="text-right">
                    <Typography variant="h3" component="h1" gutterBottom className="theme-text-primary" sx={{ fontFamily: 'Roboto', fontWeight: 800, fontSize: '2.5rem', letterSpacing: '0.5px' }}>
                      Making Tax Digital
                    </Typography>
                    <Typography variant="subtitle1" className="theme-text-secondary" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                      Tax Year {currentTaxYear}
                    </Typography>
                  </div>
                </div>

                {/* Taxpayer switcher: admins see the org books plus their own */}
                {isAdmin && (
                  <Box mt={3}>
                    <Tabs
                      value={entity}
                      onChange={(_, v) => setEntity(v)}
                      sx={{
                        '& .MuiTab-root': { color: 'rgba(255, 255, 255, 0.65)', textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: '#fff' },
                        '& .MuiTabs-indicator': { backgroundColor: '#64B5F6' }
                      }}
                    >
                      <Tab label="Organisation" value="organisation" />
                      <Tab label="My Books" value="counsellor" />
                    </Tabs>
                  </Box>
                )}
              </div>
            </div>

            {/* HMRC connection banner */}
            <Box sx={{
              mb: 4, p: 3, borderRadius: '16px',
              border: `1px solid ${readyToSubmit ? 'rgba(74,222,128,0.3)' : 'rgba(251,191,36,0.3)'}`,
              background: readyToSubmit ? 'rgba(74,222,128,0.06)' : 'rgba(251,191,36,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2
            }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {readyToSubmit ? <CheckCircleIcon sx={{ color: '#4ade80' }} /> : <WarningIcon sx={{ color: '#fbbf24' }} />}
                <Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                    {readyToSubmit
                      ? `HMRC connected${hmrcStatus?.businessName ? ` — ${hmrcStatus.businessName}` : ''}`
                      : 'HMRC filing is not ready'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    {!hmrcStatus?.configured
                      ? 'HMRC credentials are not configured on the server.'
                      : !hmrcStatus?.connected
                      ? 'Connect this taxpayer to HMRC to enable quarterly filing.'
                      : [
                          !hmrcStatus.ninoSet && 'National Insurance number required',
                          !hmrcStatus.businessId && 'select your self-employment business'
                        ].filter(Boolean).join(' — then ')}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                {hmrcStatus?.configured && !hmrcStatus.connected && (
                  <Button variant="contained" size="small" startIcon={<LinkIcon />} onClick={connectHmrc} sx={{ textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
                    Connect to HMRC
                  </Button>
                )}
                {hmrcStatus?.connected && !hmrcStatus.ninoSet && (
                  <Button variant="outlined" size="small" onClick={() => setNinoDialog(true)} sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>
                    Set NINO
                  </Button>
                )}
                {hmrcStatus?.connected && hmrcStatus.ninoSet && !hmrcStatus.businessId && (
                  <Button variant="outlined" size="small" onClick={loadBusinesses} sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.3)', color: '#fff' }}>
                    Select Business
                  </Button>
                )}
                {hmrcStatus?.connected && (
                  <Button variant="text" size="small" startIcon={<LinkOffIcon />} onClick={disconnectHmrc} sx={{ textTransform: 'none', color: '#94a3b8' }}>
                    Disconnect
                  </Button>
                )}
              </Box>
            </Box>

            {/* Alerts */}
            {(obligations.overdue > 0 || compliance.missingReceipts > 0) && (
              <Box mb={4} display="flex" flexDirection="column" gap={2}>
                {obligations.overdue > 0 && (
                  <Alert severity="error" variant="filled" icon={<WarningIcon />} sx={{ borderRadius: '12px', bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    Action Required: You have {obligations.overdue} overdue quarterly submission{obligations.overdue > 1 ? 's' : ''}.
                  </Alert>
                )}
                {compliance.missingReceipts > 0 && (
                  <Alert severity="warning" variant="filled" icon={<ReceiptIcon />} sx={{ borderRadius: '12px', bgcolor: 'rgba(245, 158, 11, 0.2)', color: '#fcd34d', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                    Compliance Check: {compliance.missingReceipts} missing receipt{compliance.missingReceipts > 1 ? 's' : ''} detected.
                  </Alert>
                )}
              </Box>
            )}

            {/* YTD Stats */}
            <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>Year To Date Overview</Typography>
            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Income" amount={formatCurrency(yearToDate.totalIncome)} type="success" icon={TrendingUpIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Expenses" amount={formatCurrency(yearToDate.totalExpenses)} type="error" icon={TrendingDownIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Net Profit" amount={formatCurrency(yearToDate.profit)} type={yearToDate.profit >= 0 ? 'success' : 'error'} icon={WalletIcon} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title={vat.requiresRegistration ? 'VAT Threshold Reached' : 'VAT Threshold'}
                  amount={`${Math.round(vat.percentageOfThreshold)}%`}
                  type={vat.requiresRegistration ? 'error' : vat.approaching ? 'warning' : 'info'}
                  icon={ReceiptIcon}
                />
              </Grid>
            </Grid>

            {/* Current Quarter Card */}
            <Box sx={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', p: 4, mb: 6 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
                <Box>
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                      Current Quarter (Q{currentQuarter.quarter})
                    </Typography>
                    <Chip label={currentQuarter.status.toUpperCase()} color={getStatusColor(currentQuarter.status) as any} size="small" sx={{ fontWeight: 700, borderRadius: '6px' }} />
                  </Box>
                  <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                    {formatDate(currentQuarter.periodStart)} — {formatDate(currentQuarter.periodEnd)}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: 0.5 }}>Deadline Status</Typography>
                  <Typography variant="h6" sx={{ color: currentQuarter.daysRemaining !== null && currentQuarter.daysRemaining < 7 ? '#f87171' : '#fff', fontWeight: 600 }}>
                    {currentQuarter.daysRemaining !== null
                      ? `${Math.max(0, currentQuarter.daysRemaining)} Days Remaining`
                      : 'No Deadline Set'}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>Income</Typography>
                  <Typography variant="h5" sx={{ color: '#4ade80', fontWeight: 600 }}>{formatCurrency(currentQuarter.income.total)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>Expenses</Typography>
                  <Typography variant="h5" sx={{ color: '#f87171', fontWeight: 600 }}>{formatCurrency(currentQuarter.expenses.total)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>Est. Profit</Typography>
                  <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>{formatCurrency(currentQuarter.profit)}</Typography>
                </Grid>
              </Grid>

              {currentQuarter.status === 'draft' && (
                <Box mt={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    onClick={() => { setSelectedQuarter(currentQuarter.quarter); setQuarterDetailsOpen(true); }}
                    sx={{ py: 1.5, bgcolor: '#3b82f6', fontWeight: 600, '&:hover': { bgcolor: '#2563eb' } }}
                  >
                    Review & Submit Return
                  </Button>
                </Box>
              )}
            </Box>

            {/* Records */}
            <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>Records</Typography>
            <Box sx={{ mb: 6, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
              <Tabs
                value={recordsTab}
                onChange={(_, v) => setRecordsTab(v)}
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none', fontWeight: 600 },
                  '& .Mui-selected': { color: '#60a5fa' },
                  '& .MuiTabs-indicator': { backgroundColor: '#60a5fa' }
                }}
              >
                <Tab label={`Income (${incomeRecords.length})`} value="income" />
                <Tab label={`Expenses (${expenseRecords.length})`} value="expense" />
              </Tabs>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)' }}>Date</TableCell>
                      <TableCell sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)' }}>Description</TableCell>
                      <TableCell sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)' }}>Type</TableCell>
                      <TableCell align="right" sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)' }}>Amount</TableCell>
                      <TableCell sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)' }}>Status</TableCell>
                      <TableCell align="right" sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.1)' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recordsTab === 'income' && incomeRecords.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ color: '#64748b', py: 4 }}>No income records yet</TableCell></TableRow>
                    )}
                    {recordsTab === 'income' && incomeRecords.map((inc) => (
                      <TableRow key={inc._id}>
                        <TableCell sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.05)' }}>{formatDate(inc.date)}</TableCell>
                        <TableCell sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.05)' }}>{inc.description}</TableCell>
                        <TableCell sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.05)' }}>{INCOME_TYPES.find(t => t.value === inc.incomeType)?.label || inc.incomeType}{inc.source !== 'manual' ? ' (auto)' : ''}</TableCell>
                        <TableCell align="right" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.05)' }}>{formatCurrency(inc.grossAmount)}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                          <Chip label={inc.submittedToHMRC ? 'Filed' : 'Draft'} size="small" sx={{ bgcolor: inc.submittedToHMRC ? 'rgba(74,222,128,0.15)' : 'rgba(148,163,184,0.15)', color: inc.submittedToHMRC ? '#4ade80' : '#cbd5e1' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                          {!inc.submittedToHMRC && (
                            <>
                              <IconButton size="small" onClick={() => openIncomeDialog(inc)} sx={{ color: '#60a5fa' }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => deleteIncome(inc._id)} sx={{ color: '#f87171' }}><DeleteIcon fontSize="small" /></IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {recordsTab === 'expense' && expenseRecords.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ color: '#64748b', py: 4 }}>No expense records yet</TableCell></TableRow>
                    )}
                    {recordsTab === 'expense' && expenseRecords.map((exp) => (
                      <TableRow key={exp._id}>
                        <TableCell sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.05)' }}>{formatDate(exp.date)}</TableCell>
                        <TableCell sx={{ color: '#cbd5e1', borderColor: 'rgba(255,255,255,0.05)' }}>{exp.description} <Typography variant="caption" sx={{ color: '#64748b' }}>({exp.supplier})</Typography></TableCell>
                        <TableCell sx={{ color: '#94a3b8', borderColor: 'rgba(255,255,255,0.05)' }}>{EXPENSE_TYPES.find(t => t.value === exp.expenseType)?.label || exp.expenseType}</TableCell>
                        <TableCell align="right" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.05)' }}>{formatCurrency(exp.allowableAmount)}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                          <Chip label={exp.submittedToHMRC ? 'Filed' : 'Draft'} size="small" sx={{ bgcolor: exp.submittedToHMRC ? 'rgba(74,222,128,0.15)' : 'rgba(148,163,184,0.15)', color: exp.submittedToHMRC ? '#4ade80' : '#cbd5e1' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                          {!exp.submittedToHMRC && (
                            <>
                              <IconButton size="small" onClick={() => openExpenseDialog(exp)} sx={{ color: '#60a5fa' }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => deleteExpense(exp._id)} sx={{ color: '#f87171' }}><DeleteIcon fontSize="small" /></IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Quarter Grid */}
            <Typography variant="h6" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>Quarterly Roadmap</Typography>
            <Grid container spacing={3}>
              {obligations.all.map((obligation) => (
                <Grid item xs={12} sm={6} md={3} key={obligation._id}>
                  <Box sx={{
                    bgcolor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', p: 3, height: '100%',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'all 0.2s',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(0,0,0,0.4)' }
                  }}>
                    <Box>
                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>Q{obligation.quarter}</Typography>
                        <Chip
                          label={obligation.status}
                          size="small"
                          sx={{
                            bgcolor: obligation.status === 'fulfilled' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                            color: obligation.status === 'fulfilled' ? '#4ade80' : '#cbd5e1',
                            fontWeight: 600,
                            border: '1px solid transparent',
                            borderColor: obligation.status === 'fulfilled' ? 'rgba(74, 222, 128, 0.2)' : 'rgba(148, 163, 184, 0.2)'
                          }}
                        />
                      </Box>
                      <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>Period</Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1 }}>
                        {formatDate(obligation.periodStart)} - {formatDate(obligation.periodEnd)}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#64748b' }}>Due {formatDate(obligation.dueDate)}</Typography>
                    </Box>

                    {obligation.status === 'open' || obligation.status === 'overdue' ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.2)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.05)' } }}
                        onClick={() => { setSelectedQuarter(obligation.quarter); setQuarterDetailsOpen(true); }}
                      >
                        View Details
                      </Button>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#4ade80', display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
                        <CheckCircleIcon fontSize="small" /> Filed
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </div>
        </div>

        {/* Quarter details / review dialog */}
        <Dialog open={quarterDetailsOpen} onClose={() => setQuarterDetailsOpen(false)} maxWidth="lg" fullWidth
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)' } } }}
          PaperProps={{ sx: { ...dialogPaperSx, overflow: 'hidden' } }}
        >
          <DialogTitle sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(0,0,0,0.2)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                Quarter {selectedQuarter} Report — {currentTaxYear}
              </Typography>
              <IconButton onClick={() => setQuarterDetailsOpen(false)} sx={{ color: '#94a3b8', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 4 }}>
            {selectedQuarter && (
              <QuarterReview entity={entity} taxYear={currentTaxYear} quarter={selectedQuarter} formatCurrency={formatCurrency} />
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(0,0,0,0.2)' }}>
            <Button onClick={() => setQuarterDetailsOpen(false)} sx={{ color: '#94a3b8', '&:hover': { color: '#fff' } }}>
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={!readyToSubmit}
              title={!readyToSubmit ? 'Connect HMRC, set NINO and select a business first' : ''}
              onClick={() => setSubmitConfirm(true)}
              sx={{ bgcolor: '#4ade80', color: '#0f172a', fontWeight: 700, '&:hover': { bgcolor: '#22c55e' } }}
            >
              Finalise & Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submit confirmation */}
        <Dialog open={submitConfirm} onClose={() => setSubmitConfirm(false)} PaperProps={{ sx: dialogPaperSx }}>
          <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>Submit Q{selectedQuarter} to HMRC?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#94a3b8' }}>
              This files the quarterly update to HMRC for {currentTaxYear} Q{selectedQuarter} and marks the underlying records as submitted (locking them from further edits).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmitConfirm(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
            <Button variant="contained" onClick={submitQuarter} disabled={submitting} sx={{ bgcolor: '#4ade80', color: '#0f172a', fontWeight: 700 }}>
              {submitting ? 'Submitting…' : 'Confirm Submission'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Income dialog */}
        <Dialog open={incomeDialog.open} onClose={() => setIncomeDialog({ open: false, editing: null })} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
          <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>{incomeDialog.editing ? 'Edit Income' : 'Add Income'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField label="Date" type="date" value={incomeForm.date} onChange={e => setIncomeForm({ ...incomeForm, date: e.target.value })} InputLabelProps={{ shrink: true }} sx={inputSx} required />
            <TextField select label="Income Type" value={incomeForm.incomeType} onChange={e => setIncomeForm({ ...incomeForm, incomeType: e.target.value })} sx={inputSx}>
              {INCOME_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField label="Amount (£)" type="number" value={incomeForm.grossAmount} onChange={e => setIncomeForm({ ...incomeForm, grossAmount: e.target.value })} sx={inputSx} required inputProps={{ min: 0, step: 0.01 }} />
            <TextField label="Description" value={incomeForm.description} onChange={e => setIncomeForm({ ...incomeForm, description: e.target.value })} sx={inputSx} required />
            <TextField label="Invoice Number (optional)" value={incomeForm.invoiceNumber} onChange={e => setIncomeForm({ ...incomeForm, invoiceNumber: e.target.value })} sx={inputSx} />
            <TextField select label="Payment Method" value={incomeForm.paymentMethod} onChange={e => setIncomeForm({ ...incomeForm, paymentMethod: e.target.value })} sx={inputSx}>
              {PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Checkbox checked={incomeForm.paymentReceived} onChange={e => setIncomeForm({ ...incomeForm, paymentReceived: e.target.checked })} sx={{ color: '#94a3b8' }} />}
              label={<Typography sx={{ color: '#cbd5e1' }}>Payment received</Typography>}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIncomeDialog({ open: false, editing: null })} sx={{ color: '#94a3b8' }}>Cancel</Button>
            <Button variant="contained" onClick={saveIncome} sx={{ bgcolor: '#4caf50', fontWeight: 700 }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Expense dialog */}
        <Dialog open={expenseDialog.open} onClose={() => setExpenseDialog({ open: false, editing: null })} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
          <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>{expenseDialog.editing ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField label="Date" type="date" value={expenseForm.date} onChange={e => setExpenseForm({ ...expenseForm, date: e.target.value })} InputLabelProps={{ shrink: true }} sx={inputSx} required />
            <TextField select label="Expense Type" value={expenseForm.expenseType} onChange={e => setExpenseForm({ ...expenseForm, expenseType: e.target.value })} sx={inputSx}>
              {EXPENSE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField label="Amount (£)" type="number" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} sx={inputSx} required inputProps={{ min: 0, step: 0.01 }} />
            <TextField label="Supplier" value={expenseForm.supplier} onChange={e => setExpenseForm({ ...expenseForm, supplier: e.target.value })} sx={inputSx} required />
            <TextField label="Description" value={expenseForm.description} onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })} sx={inputSx} required />
            <TextField label="Allowable %" type="number" value={expenseForm.allowablePercentage} onChange={e => setExpenseForm({ ...expenseForm, allowablePercentage: e.target.value })} sx={inputSx} inputProps={{ min: 0, max: 100 }} helperText={<Typography variant="caption" sx={{ color: '#64748b' }}>For mixed-use expenses (e.g. home office)</Typography>} />
            <TextField label="Receipt URL (optional)" value={expenseForm.receiptUrl} onChange={e => setExpenseForm({ ...expenseForm, receiptUrl: e.target.value })} sx={inputSx} />
            <FormControlLabel
              control={<Checkbox checked={expenseForm.hasReceipt} onChange={e => setExpenseForm({ ...expenseForm, hasReceipt: e.target.checked })} sx={{ color: '#94a3b8' }} />}
              label={<Typography sx={{ color: '#cbd5e1' }}>Receipt held</Typography>}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExpenseDialog({ open: false, editing: null })} sx={{ color: '#94a3b8' }}>Cancel</Button>
            <Button variant="contained" onClick={saveExpense} sx={{ bgcolor: '#f44336', fontWeight: 700 }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* NINO dialog */}
        <Dialog open={ninoDialog} onClose={() => setNinoDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
          <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>National Insurance Number</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              label="NINO" fullWidth value={ninoValue}
              onChange={e => setNinoValue(e.target.value)}
              placeholder="QQ 12 34 56 C"
              sx={inputSx}
              helperText={<Typography variant="caption" sx={{ color: '#64748b' }}>Stored encrypted; required for HMRC submission</Typography>}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNinoDialog(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
            <Button variant="contained" onClick={saveNino} disabled={!ninoValue.trim()} sx={{ bgcolor: '#3b82f6', fontWeight: 700 }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Business selector dialog */}
        <Dialog open={businessDialog} onClose={() => setBusinessDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
          <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>Select HMRC Business</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {businesses.length === 0 ? (
              <Typography sx={{ color: '#94a3b8' }}>No self-employment businesses were returned by HMRC.</Typography>
            ) : (
              <TextField select label="Business" fullWidth value={selectedBusiness} onChange={e => setSelectedBusiness(e.target.value)} sx={inputSx}>
                {businesses.map(b => <MenuItem key={b.businessId} value={b.businessId}>{b.businessName || b.businessId}</MenuItem>)}
              </TextField>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBusinessDialog(false)} sx={{ color: '#94a3b8' }}>Cancel</Button>
            <Button variant="contained" onClick={saveBusiness} disabled={!selectedBusiness} sx={{ bgcolor: '#3b82f6', fontWeight: 700 }}>Select</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
        </Snackbar>
      </div>
    </ThemeBackground>
  );
};

/**
 * Quarter review inside the details dialog: fetches the quarterly summary
 * fresh so the totals reflect the latest records.
 */
const QuarterReview: React.FC<{
  entity: 'counsellor' | 'organisation';
  taxYear: string;
  quarter: number;
  formatCurrency: (n: number) => string;
}> = ({ entity, taxYear, quarter, formatCurrency }) => {
  const [summary, setSummary] = useState<MTDQuarterlyUpdate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/mtd/quarterly/${taxYear}/${quarter}`, { params: { entity } });
        setSummary(res.data.summary);
      } finally {
        setLoading(false);
      }
    })();
  }, [entity, taxYear, quarter]);

  if (loading || !summary) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} sx={{ color: '#fff' }} /></Box>;
  }

  const expenseRows = [
    { l: 'Staff Costs', v: summary.expenses.staffCosts },
    { l: 'Travel', v: summary.expenses.travelCosts },
    { l: 'Premises', v: summary.expenses.premisesRunningCosts },
    { l: 'Admin', v: summary.expenses.adminCosts },
    { l: 'Advertising', v: summary.expenses.advertisingCosts },
    { l: 'Professional Fees', v: summary.expenses.professionalFees },
    { l: 'Cost of Goods', v: summary.expenses.costOfGoodsBought },
    { l: 'Other', v: summary.expenses.otherExpenses }
  ].filter(r => r.v > 0);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>Turnover</Typography>
        <Typography variant="h5" sx={{ color: '#4ade80', fontWeight: 600 }}>{formatCurrency(summary.income.total)}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>Expenses</Typography>
        <Typography variant="h5" sx={{ color: '#f87171', fontWeight: 600 }}>{formatCurrency(summary.expenses.total)}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>Profit</Typography>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>{formatCurrency(summary.profit)}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
        <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 1 }}>Expense breakdown (HMRC categories)</Typography>
        {expenseRows.length === 0 && <Typography variant="body2" sx={{ color: '#64748b' }}>No expenses recorded this quarter.</Typography>}
        {expenseRows.map(r => (
          <Box key={r.l} display="flex" justifyContent="space-between" py={0.5}>
            <Typography variant="body2" sx={{ color: '#cbd5e1' }}>{r.l}</Typography>
            <Typography variant="body2" sx={{ color: '#fff', fontFamily: 'monospace' }}>{formatCurrency(r.v)}</Typography>
          </Box>
        ))}
      </Grid>
      {summary.status === 'submitted' && (
        <Grid item xs={12}>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ bgcolor: 'rgba(74,222,128,0.1)', color: '#4ade80' }}>
            Submitted to HMRC{summary.submissionDate ? ` on ${new Date(summary.submissionDate).toLocaleString('en-GB')}` : ''}.
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default MTDDashboard;
